import merge from 'deepmerge';
import { DateRange, isNullOrUndefined, UTC } from 'rewire-common';
import { freeze, observable } from 'rewire-core';
import {
  AvailableBalance,
  BatchStatusType,
  FieldFilter,
  GroupViewUpsertInput,
  INameLike,
  IUdf,
  MessageLevel,
  ReasonCategory,
  ScheduleFilterInput,
  ScheduleSort,
  scheduleFilterInputIdFromKey,
  TimecardTestStatusType,
  toBatchStatusType,
} from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import DataQueries from '../graphql/Queries';
import mainPageModel from '../main-navigation/MainPageModel';
import { RouteManagerBase, reportRouteManager } from '../main-navigation/RouteManager';
import udfsSubscriptionService from '../services/UdfsSubscriptionService';
import searchViewDialogModel, { ISearchViewDialogData } from '../side-navigation/SearchViewDialogModel';
import { notifications } from '../ui/Notifications';
import { IPriorityGroupMenuItem, PermittedPriorityGroupMenuModel } from '../ui/PriorityGroupMenuModel';
import { IPrioritySortMenuItem, PermittedPrioritySortMenuModel } from '../ui/PrioritySortMenuModel';
import { objectKeys } from '../utility/Object';
import scheduleFilterDialogModel from './ScheduleFilterDialogModel';
import scheduleModel from './ScheduleModel';

export interface IScheduleControlsData {
  filters?: ScheduleFilterInput;
  groups?: string[];
  sorts?: ScheduleSort;
}

export type ScheduleControlsModelMode = 'group' | 'view' | 'search';

export interface IScheduleFilters {
  areas?: INameLike[];
  groups?: INameLike[];
  occupations?: INameLike[];
  assignedEmployees?: INameLike[];
  templates?: INameLike[];
  generators?: INameLike[];
  reasons?: INameLike[];
  batches?: INameLike[];
  levels?: MessageLevel[];
  messageLevels?: MessageLevel[];
  inBatch?: boolean;
  batchStatus?: BatchStatusType[];
  availableBalance?: AvailableBalance[];
  timecardStatus?: string[];
  timeOffStatus?: string[];
  timeOffTypes?: string[];
  timecardTestStatus?: TimecardTestStatusType;
  effective?: DateRange;
  lastRanAfter?: UTC;
  tags?: string[];
  search?: string;
  custom?: FieldFilter[];
}

interface IScheduleControlsModelState {
  scheduleFilters: IScheduleFilters;
  areaId?: string;
  groupId?: string;
  viewId?: string;
}

export class ScheduleControlsModel {
  state: IScheduleControlsModelState;
  groupModel: PermittedPriorityGroupMenuModel;
  sortModel: PermittedPrioritySortMenuModel;

  constructor() {
    this.state = observable({
      scheduleFilters: {} as IScheduleFilters,
      areaId: undefined,
      groupId: undefined,
      viewId: undefined,
    });

    this.groupModel = new PermittedPriorityGroupMenuModel(this.apply, this.getPriorityGroupItemsFn());
    this.sortModel = new PermittedPrioritySortMenuModel(this.apply, this.getPrioritySortItemsFn());
  }

  load = async () => {
    const { areaId, groupId, viewId, allFilters, allGroups, allSorts } = RouteManagerBase.current;

    let controlsData: IScheduleControlsData = {
      filters: allFilters && merge({}, allFilters),
      groups: allGroups && [...allGroups],
      sorts: allSorts && merge({}, allSorts),
    };

    const areaMap = mainPageModel.areaMap;
    const groupMap = mainPageModel.groupMap;
    const viewMap = mainPageModel.viewMap;
    if (groupId) {
      controlsData.filters = controlsData.filters ? controlsData.filters : {};
      if (areaId) controlsData.filters['areaIds'] = [areaId];
      controlsData.filters['groupIds'] = [groupId!];
    } else if (viewId) {
      controlsData = viewMap[viewId!]?.options ? merge({}, viewMap[viewId!].options!) : {};
    }

    // remove duplicate filter ids
    controlsData.filters &&
      objectKeys(controlsData.filters).forEach((key) => {
        if (Array.isArray(controlsData.filters![key]) && controlsData.filters![key]?.length) {
          (controlsData as any).filters![key] = [...new Set(controlsData.filters![key])];
        }
      });

    // remove duplicate groups
    controlsData.groups = [...new Set(controlsData.groups)];

    let scheduleFilters: IScheduleFilters = {};
    if (controlsData.filters) {
      if (controlsData.filters.areaIds)
        scheduleFilters.areas = controlsData.filters.areaIds.map((areaId: string) => ({ id: areaId, name: areaMap[areaId]?.name }));
      if (controlsData.filters.groupIds)
        scheduleFilters.groups = controlsData.filters.groupIds.map((groupId: string) => ({
          id: groupId,
          name: groupMap[groupId]?.name,
        }));
      if (controlsData.filters.levels) scheduleFilters.levels = controlsData.filters.levels;
      if (controlsData.filters.messageLevels) scheduleFilters.messageLevels = controlsData.filters.messageLevels;
      if (!isNullOrUndefined(controlsData.filters.inBatch)) scheduleFilters.inBatch = controlsData.filters.inBatch;
      if (controlsData.filters.batchStatus) scheduleFilters.batchStatus = controlsData.filters.batchStatus;
      if (controlsData.filters.timecardStatus) scheduleFilters.timecardStatus = controlsData.filters.timecardStatus;
      if (controlsData.filters.timeOffStatus) scheduleFilters.timeOffStatus = controlsData.filters.timeOffStatus;
      if (controlsData.filters.timeOffTypes) scheduleFilters.timeOffTypes = controlsData.filters.timeOffTypes;
      if (controlsData.filters.availableBalance) scheduleFilters.availableBalance = controlsData.filters.availableBalance;
      if (controlsData.filters.status) scheduleFilters.timecardTestStatus = controlsData.filters.status;
      if (controlsData.filters.effective) scheduleFilters.effective = controlsData.filters.effective;
      if (controlsData.filters.lastRanAfter) scheduleFilters.lastRanAfter = controlsData.filters.lastRanAfter;
      if (controlsData.filters.tags) scheduleFilters.tags = controlsData.filters.tags;
      if (controlsData.filters.search) scheduleFilters.search = controlsData.filters.search;
      if (controlsData.filters.custom) scheduleFilters.custom = controlsData.filters.custom;
      scheduleFilters = Object.assign(scheduleFilters, await this.getRestOfFilterData(controlsData.filters));
    }
    freeze(() => {
      this.scheduleFilters = scheduleFilters;
      this.groupModel.load(controlsData.groups || ([] as string[]));
      this.sortModel.load(controlsData.sorts);
      this.areaId = areaId;
      this.groupId = groupId;
      this.viewId = viewId;
    });
  };

  apply = () => {
    setTimeout(() => {
      const filterIds = this.scheduleFilterIds;
      const scheduleGroups = this.scheduleGroups;
      const scheduleSorts = this.scheduleSorts;
      if (this.mode === 'view') {
        RouteManagerBase.current.navigateTo({
          filters: filterIds,
          groups: scheduleGroups,
          sorts: scheduleSorts,
          areaId: undefined,
          groupId: undefined,
          viewId: undefined,
        });
      } else {
        RouteManagerBase.current.navigateTo({ filters: filterIds, groups: scheduleGroups, sorts: scheduleSorts });
      }
    }, 0);
  };

  getPriorityGroupItemsFn(): () => IPriorityGroupMenuItem[] {
    const isGroupPermitted = this.isGroupPermitted;
    return () => {
      return [
        { name: 'area', title: 'Area', visible: () => isGroupPermitted('area'), disabled: () => this.mode === 'group' },
        { name: 'group', title: 'Group', visible: () => isGroupPermitted('group'), disabled: () => this.mode === 'group' },
        { name: 'occupation', title: 'Occupation', visible: () => isGroupPermitted('occupation') },
        { name: 'generator', title: 'Shift Pattern', visible: () => isGroupPermitted('generator') },
        { name: 'template', title: 'Shift Type', visible: () => isGroupPermitted('template') },
        { name: 'linePosition', title: 'Line Position', visible: () => isGroupPermitted('linePosition') },
        { name: 'workOrder', title: 'Work Order', visible: () => isGroupPermitted('workOrder') },
        { name: 'position', title: 'Position', visible: () => isGroupPermitted('position') },
        { name: 'job', title: 'Job', visible: () => isGroupPermitted('job') },
        { name: 'team', title: 'Team', visible: () => isGroupPermitted('team') },
        { name: 'employee', title: 'Employee', visible: () => isGroupPermitted('employee') },
        { name: 'batch', title: 'Batch', visible: () => isGroupPermitted('batch') },
        { name: 'accountCode', title: 'Account Code', visible: () => isGroupPermitted('accountCode') },
        ...udfsSubscriptionService.subscriptionModel.udfs.map((udf: IUdf) => ({
          name: udf.name,
          title: udf.displayName,
          active: true,
          visible: () => isGroupPermitted(udf.name),
        })),
      ];
    };
  }

  getPrioritySortItemsFn(): () => IPrioritySortMenuItem[] {
    const isSortPermitted = this.isSortPermitted;
    return () => {
      return [
        {
          name: 'area',
          title: 'Area',
          visible: () => isSortPermitted('area'),
          disabled: () => scheduleControlsModel.mode === 'group',
        },
        {
          name: 'group',
          title: 'Group',
          visible: () => isSortPermitted('group'),
          disabled: () => scheduleControlsModel.mode === 'group',
        },
        { name: 'occupation', title: 'Occupation', visible: () => isSortPermitted('occupation') },
        { name: 'generator', title: 'Shift Pattern', visible: () => isSortPermitted('generator') },
        { name: 'employee', title: 'Employee', visible: () => isSortPermitted('employee') },
        { name: 'template', title: 'Shift Type', visible: () => isSortPermitted('template') },
        { name: 'accountCode', title: 'AccountCode', visible: () => isSortPermitted('accountCode') },
        { name: 'employeeCode', title: 'Employee Code', visible: () => isSortPermitted('employeeCode') },
        { name: 'batch', title: 'Batch', visible: () => isSortPermitted('batch') },
        ...udfsSubscriptionService.subscriptionModel.udfs.map((udf: IUdf) => ({
          name: udf.name,
          title: udf.displayName,
          visible: () => isSortPermitted(udf.name),
        })),
      ];
    };
  }

  getRestOfFilterData = async (filterData: ScheduleFilterInput) => {
    const restFilterPromises: any = {};
    const returnSchema = `
      id
      name
    `;

    if (filterData.occupationIds)
      restFilterPromises['occupations'] = () => DataQueries.getOccupationsByIds(returnSchema, filterData.occupationIds!);
    if (filterData.assignedEmployeeIds)
      restFilterPromises['assignedEmployees'] = () => DataQueries.getEmployeesByIds(returnSchema, filterData.assignedEmployeeIds!);
    if (filterData.employeeIds)
      restFilterPromises['employees'] = () => DataQueries.getEmployeesByIds(returnSchema, filterData.employeeIds!);
    if (filterData.templateIds)
      restFilterPromises['templates'] = () => DataQueries.getTemplatesByIds(returnSchema, filterData.templateIds!);
    if (filterData.generatorIds)
      restFilterPromises['generators'] = () => DataQueries.getGeneratorsByIds(returnSchema, filterData.generatorIds!);
    if (filterData.reasonIds)
      restFilterPromises['reasons'] = () => DataQueries.getReasonsByIds(returnSchema, filterData.reasonIds!);
    if (filterData.batchIds) restFilterPromises['batches'] = () => DataQueries.getBatchesByIds(returnSchema, filterData.batchIds!);

    if (Object.keys(restFilterPromises).length) {
      const result = await Promise.all(
        Object.keys(restFilterPromises).map(async (key: string) => ({ [key]: await restFilterPromises[key]() })),
      );
      return result?.reduce((acc, currVal) => Object.assign(acc, currVal)) || {};
    }
    return {};
  };

  get scheduleFilters(): IScheduleFilters {
    return this.state.scheduleFilters;
  }

  set scheduleFilters(scheduleFilters: IScheduleFilters) {
    this.state.scheduleFilters = scheduleFilters;
  }

  get scheduleFilterIds(): ScheduleFilterInput {
    const filterIds: ScheduleFilterInput = {};
    objectKeys(this.scheduleFilters).forEach((key) => {
      const id = scheduleFilterInputIdFromKey(key as any) ?? key;
      switch (key) {
        case 'levels':
        case 'messageLevels': {
          const levels: any = this.scheduleFilters[key];
          if (levels) {
            filterIds[key] = levels.slice();
          }
          break;
        }

        case 'inBatch': {
          const inBatch: any = this.scheduleFilters[key];
          if (!isNullOrUndefined(inBatch)) {
            filterIds[key] = inBatch;
          }
          break;
        }

        case 'batchStatus': {
          const entry: any = this.scheduleFilters[key];
          if (entry) {
            filterIds[key] = Array.isArray(entry) ? [entry[0]] : [toBatchStatusType(entry)];
          }
          break;
        }

        case 'timecardStatus': {
          const timecardStatus: any = this.scheduleFilters[key];
          if (timecardStatus) {
            filterIds[key] = timecardStatus.slice();
          }
          break;
        }

        case 'timeOffStatus': {
          const timeOffStatus: any = this.scheduleFilters[key];
          if (timeOffStatus) {
            filterIds[key] = timeOffStatus;
          }
          break;
        }

        case 'timeOffTypes': {
          const timeOffTypes: any = this.scheduleFilters[key];
          if (timeOffTypes) {
            filterIds[key] = timeOffTypes;
          }
          break;
        }

        case 'availableBalance': {
          const availableBalance: any = this.scheduleFilters[key];
          if (availableBalance) {
            filterIds[key] = availableBalance;
          }
          break;
        }

        case 'timecardTestStatus': {
          const timecardTestStatus: any = this.scheduleFilters[key];
          if (timecardTestStatus) {
            (filterIds as any)[id] = timecardTestStatus;
          }
          break;
        }

        case 'effective': {
          const effective: any = this.scheduleFilters[key];
          if (effective) {
            filterIds[key] = effective;
          }
          break;
        }

        case 'lastRanAfter': {
          const lastRanAfter: any = this.scheduleFilters[key];
          if (lastRanAfter) {
            filterIds[key] = lastRanAfter;
          }
          break;
        }

        case 'tags': {
          const tags: any = this.scheduleFilters[key];
          if (tags) {
            filterIds[key] = tags;
          }
          break;
        }

        case 'search': {
          const search: any = this.scheduleFilters[key];
          if (search) {
            filterIds[key] = search;
          }
          break;
        }

        case 'custom': {
          const custom: any = this.scheduleFilters[key];
          if (custom) {
            filterIds[key] = custom.slice();
          }
          break;
        }

        default: {
          const filter = this.scheduleFilters[key];
          (filterIds as any)[id] =
            (Array.isArray(filter) &&
              filter.length > 0 &&
              filter.map((searchEntity: INameLike) => {
                return searchEntity.id;
              })) ||
            undefined;
        }
      }
    });
    return filterIds;
  }

  get scheduleFilterIdsPermitted(): ScheduleFilterInput {
    return RouteManagerBase.current.getPermittedScheduleFilterInput(this.scheduleFilterIds) ?? {};
  }

  get scheduleGroups(): string[] {
    return this.groupModel.groups;
  }

  set scheduleGroups(scheduleGroups: string[]) {
    this.groupModel.groups = scheduleGroups;
  }

  get scheduleGroupsPermitted(): string[] {
    const groups: string[] = [];
    this.scheduleGroups.forEach((group: string) => {
      if (!this.isGroupPermitted(group)) return;
      groups.push(group);
    });
    return groups;
  }

  get scheduleSorts(): ScheduleSort {
    return this.sortModel.sorts;
  }

  set scheduleSorts(scheduleSorts: ScheduleSort) {
    this.sortModel.sorts = scheduleSorts;
  }

  get scheduleSortsPermitted(): ScheduleSort {
    return RouteManagerBase.current.getPermittedScheduleSort(this.scheduleSorts) ?? {};
  }

  get areaId(): string | undefined {
    return this.state.areaId;
  }

  set areaId(areaId: string | undefined) {
    this.state.areaId = areaId;
  }

  get groupId(): string | undefined {
    return this.state.groupId;
  }

  set groupId(groupId: string | undefined) {
    this.state.groupId = groupId;
  }

  get viewId(): string | undefined {
    return this.state.viewId;
  }

  set viewId(viewId: string | undefined) {
    this.state.viewId = viewId;
  }

  get mode(): ScheduleControlsModelMode {
    return this.groupId ? 'group' : this.viewId ? 'view' : 'search';
  }

  get hasFilters(): boolean {
    if (this.mode === 'group') {
      return this.hasGroupFilters;
    }
    return Object.keys(this.scheduleFilters).length > 0;
  }

  get hasGroupFilters(): boolean {
    const groupFilterKeys = Object.keys(this.scheduleFilters).filter((key: string) => key !== 'areas' && key !== 'groups');
    return groupFilterKeys.length > 0;
  }

  get reasonFilterCategories(): ReasonCategory[] {
    return RouteManagerBase.current.reasonFilterCategories;
  }

  get canAddViews() {
    return mainPageModel.canAddViews;
  }

  get canEditViews() {
    return mainPageModel.canEditViews;
  }

  get scheduleGridOptions() {
    return scheduleModel.routeManager.scheduleGridOptions;
  }

  get reportSecondaryFilters() {
    return reportRouteManager.reportSecondaryFilters;
  }

  isGroupPermitted(property: string): boolean {
    return RouteManagerBase.current.permittedGroups.includes(property);
  }

  isSortPermitted(property: string): boolean {
    return RouteManagerBase.current.permittedSorts.includes(property);
  }

  hasGroup(groupName: string): boolean {
    return this.scheduleGroups.includes(groupName);
  }

  saveLayoutAsDefault = async () => {
    if (this.mode !== 'group') {
      return;
    }
    const groupUpdateInput: GroupViewUpsertInput = {
      id: RouteManagerBase.current.groupId!,
      scheduleOptions: {
        filters: this.scheduleFilterIds,
        groups: this.scheduleGroups,
        sorts: this.scheduleSorts,
        scheduleGridOptions: this.scheduleGridOptions!,
      },
    };
    const result = await DataMutations.upsertGroupView(groupUpdateInput);
    if (result) {
      notifications.success('Group Default Layout Updated');
      mainPageModel.updateAreasAndGroups();
    } else {
      notifications.error('Group Default Layout Update Failed');
    }
  };

  showSearchViewDialog() {
    if (this.mode === 'group') {
      return;
    }
    const searchViewDialogData: ISearchViewDialogData = {
      options: {
        filters: this.scheduleFilterIds,
        groups: this.scheduleGroups,
        sorts: this.scheduleSorts,
      },
      fields: {
        scheduleGridOptions: this.scheduleGridOptions,
        reportSecondaryFilters: this.reportSecondaryFilters,
      },
    };
    searchViewDialogModel.load(searchViewDialogData);
  }

  clearFilters() {
    if (this.mode === 'group') this.scheduleFilters = { areas: this.scheduleFilters.areas, groups: this.scheduleFilters.groups };
    else {
      this.scheduleFilters = {};
    }
    this.apply();
  }

  showFilterDialog() {
    scheduleFilterDialogModel.load(this.scheduleFilters);
  }
}

const scheduleControlsModel = new ScheduleControlsModel();
export default scheduleControlsModel;
